import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDateNumeric } from '@/lib/dates';
import { useState } from 'react';
import {
  Activity,
  FileCode,
  AlertTriangle,
  Zap,
  RefreshCw,
  Clock,
  CheckCircle2,
  XCircle,
  Key,
  Eye,
  EyeOff,
  Settings,
} from 'lucide-react';
import {
  useHealthCheck,
  usePipelineExecutions,
  useDeadLetterQueue,
  usePollQueue,
  useStaleCheck,
  useExecutionRealtimeUpdates,
  useHermesJwt,
} from '@/components/hermes';
import { ActionButton, StatusBadge } from '@/components/hermes';
import { toast } from 'sonner';

export const Route = createFileRoute('/hermes/')({
  component: HermesDashboard,
});

function HermesDashboard() {
  const { data: health, isLoading: healthLoading } = useHealthCheck();
  const { data: executions } = usePipelineExecutions({ limit: 5 });
  const { data: dlqItems } = useDeadLetterQueue();
  const pollMutation = usePollQueue();
  const staleMutation = useStaleCheck();
  const { jwt, updateJwt, clearJwt, isAuthenticated } = useHermesJwt();
  const [showSettings, setShowSettings] = useState(false);

  // Enable realtime updates
  useExecutionRealtimeUpdates();

  const handlePoll = async () => {
    try {
      const result = await pollMutation.mutateAsync();
      toast.success(`Processed ${result.processed} tasks`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to poll queue');
    }
  };

  const handleStaleCheck = async () => {
    try {
      const result = await staleMutation.mutateAsync();
      toast.success(`Found ${result.found} stale tasks`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to check stale tasks');
    }
  };

  const runningCount = executions?.filter(e => e.status === 'running').length ?? 0;
  const completedCount = executions?.filter(e => e.status === 'completed').length ?? 0;
  const failedCount = executions?.filter(e => e.status === 'failed').length ?? 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Hermes Pipeline Manager</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor and manage pipeline executions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ActionButton onClick={handlePoll} size="sm">
            <Zap className="w-3.5 h-3.5" />
            Poll Queue
          </ActionButton>
          <ActionButton onClick={handleStaleCheck} size="sm">
            <RefreshCw className="w-3.5 h-3.5" />
            Stale Check
          </ActionButton>
          <ActionButton onClick={() => setShowSettings(!showSettings)} size="sm" variant="ghost">
            <Settings className="w-3.5 h-3.5" />
          </ActionButton>
        </div>
      </div>

      {/* JWT Configuration Panel */}
      {showSettings && (
        <JwtConfigPanel
          jwt={jwt}
          isAuthenticated={isAuthenticated}
          onSave={updateJwt}
          onClear={clearJwt}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Auth Warning Banner */}
      {!isAuthenticated && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4">
          <div className="flex items-center gap-3">
            <Key className="w-5 h-5 text-amber-400" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-400">Authentication Required</p>
              <p className="text-xs text-amber-400/80 mt-0.5">
                Enter your JWT token to execute pipelines and access the worker API.
              </p>
            </div>
            <ActionButton onClick={() => setShowSettings(true)} size="sm">
              <Key className="w-3.5 h-3.5" />
              Configure
            </ActionButton>
          </div>
        </div>
      )}

      {/* Health Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <HealthCard
          title="System Status"
          value={health?.status ?? 'unknown'}
          icon={Activity}
          loading={healthLoading}
          color={health?.status === 'healthy' ? 'emerald' : health?.status === 'degraded' ? 'amber' : 'red'}
        />
        <HealthCard
          title="Queue Depth"
          value={health?.queue_depth ?? 0}
          icon={Clock}
          loading={healthLoading}
        />
        <HealthCard
          title="Running Tasks"
          value={health?.running_tasks ?? 0}
          icon={Activity}
          loading={healthLoading}
          color="blue"
        />
        <HealthCard
          title="Dead Letter Queue"
          value={dlqItems?.length ?? 0}
          icon={AlertTriangle}
          loading={healthLoading}
          color={dlqItems && dlqItems.length > 0 ? 'red' : undefined}
        />
      </div>

      {/* Quick Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <NavCard
          to="/hermes/executions"
          title="Executions"
          description="View all pipeline runs"
          icon={Activity}
          stats={[
            { label: 'Running', value: runningCount, color: 'blue' },
            { label: 'Completed', value: completedCount, color: 'emerald' },
            { label: 'Failed', value: failedCount, color: 'red' },
          ]}
        />
        <NavCard
          to="/hermes/definitions"
          title="Definitions"
          description="Manage pipeline templates"
          icon={FileCode}
        />
        <NavCard
          to="/hermes/dlq"
          title="Dead Letter Queue"
          description="Review failed tasks"
          icon={AlertTriangle}
          badge={dlqItems && dlqItems.length > 0 ? `${dlqItems.length} items` : undefined}
          badgeColor="red"
        />
      </div>

      {/* Recent Executions */}
      <div className="rounded-lg border border-border bg-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Recent Executions</h2>
          <Link
            to="/hermes/executions"
            className="text-sm text-primary hover:underline"
          >
            View all
          </Link>
        </div>
        <div className="divide-y divide-border">
          {executions?.slice(0, 5).map((execution) => (
            <Link
              key={execution.id}
              to="/hermes/executions/$executionId"
              params={{ executionId: execution.id }}
              className="flex items-center justify-between px-4 py-3 hover:bg-secondary/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <StatusIcon status={execution.status} />
                <div>
                  <span className="font-medium text-foreground">
                    {execution.pipeline_definitions?.name ?? 'Unknown Pipeline'}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {(execution.correlation_id ?? execution.id)?.slice(0, 8) ?? 'Unknown'}...
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={execution.status} animated={execution.status === 'running'} size="sm" />
                <span className="text-xs text-muted-foreground">
                  {formatRelativeTime(execution.created_at)}
                </span>
              </div>
            </Link>
          ))}
          {(!executions || executions.length === 0) && (
            <div className="px-4 py-8 text-center text-muted-foreground">
              No executions yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Helper Components

interface HealthCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  loading?: boolean;
  color?: 'emerald' | 'amber' | 'red' | 'blue';
}

function HealthCard({ title, value, icon: Icon, loading, color }: HealthCardProps) {
  const colorClasses = {
    emerald: 'text-emerald-400',
    amber: 'text-amber-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-secondary">
          <Icon className={`w-5 h-5 ${color ? colorClasses[color] : 'text-muted-foreground'}`} />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{title}</p>
          <p className={`text-lg font-semibold ${color ? colorClasses[color] : 'text-foreground'}`}>
            {loading ? '...' : value}
          </p>
        </div>
      </div>
    </div>
  );
}

interface NavCardProps {
  to: string;
  title: string;
  description: string;
  icon: React.ElementType;
  stats?: { label: string; value: number; color: string }[];
  badge?: string;
  badgeColor?: 'red' | 'amber';
}

function NavCard({ to, title, description, icon: Icon, stats, badge, badgeColor }: NavCardProps) {
  return (
    <Link
      to={to}
      className="rounded-lg border border-border bg-card p-4 hover:bg-secondary/30 transition-colors block"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-lg bg-secondary">
          <Icon className="w-5 h-5 text-foreground" />
        </div>
        {badge && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            badgeColor === 'red' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'
          }`}>
            {badge}
          </span>
        )}
      </div>
      <h3 className="font-semibold text-foreground">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1">{description}</p>
      {stats && (
        <div className="flex gap-4 mt-3">
          {stats.map((stat) => (
            <div key={stat.label} className="text-xs">
              <span className={`font-semibold ${
                stat.color === 'blue' ? 'text-blue-400' :
                stat.color === 'emerald' ? 'text-emerald-400' :
                stat.color === 'red' ? 'text-red-400' : 'text-foreground'
              }`}>
                {stat.value}
              </span>
              <span className="text-muted-foreground ml-1">{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'running':
      return <Activity className="w-4 h-4 text-blue-400" />;
    case 'completed':
      return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-400" />;
    default:
      return <Clock className="w-4 h-4 text-muted-foreground" />;
  }
}

// JWT Configuration Panel
interface JwtConfigPanelProps {
  jwt: string | null;
  isAuthenticated: boolean;
  onSave: (token: string) => void;
  onClear: () => void;
  onClose: () => void;
}

function JwtConfigPanel({ jwt, isAuthenticated, onSave, onClear, onClose }: JwtConfigPanelProps) {
  const [tokenInput, setTokenInput] = useState(jwt ?? '');
  const [showToken, setShowToken] = useState(false);

  const handleSave = () => {
    if (tokenInput.trim()) {
      onSave(tokenInput.trim());
      toast.success('JWT token saved');
    }
  };

  const handleClear = () => {
    onClear();
    setTokenInput('');
    toast.success('JWT token cleared');
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-foreground">API Authentication</h3>
          {isAuthenticated && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
              Connected
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-secondary transition-colors"
        >
          <XCircle className="w-4 h-4 text-muted-foreground" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">JWT Token</label>
          <div className="relative">
            <input
              type={showToken ? 'text' : 'password'}
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              placeholder="Enter your Supabase JWT token..."
              className="w-full px-3 py-2 pr-10 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono"
            />
            <button
              type="button"
              onClick={() => setShowToken(!showToken)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-secondary transition-colors"
            >
              {showToken ? (
                <EyeOff className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Eye className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Get your JWT from the Supabase dashboard or by logging in via the auth API.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <ActionButton onClick={handleSave} size="sm" disabled={!tokenInput.trim()}>
            <CheckCircle2 className="w-3.5 h-3.5" />
            Save Token
          </ActionButton>
          {isAuthenticated && (
            <ActionButton onClick={handleClear} size="sm" variant="destructive">
              <XCircle className="w-3.5 h-3.5" />
              Clear
            </ActionButton>
          )}
        </div>
      </div>
    </div>
  );
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateNumeric(date);
}
